import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAxios from '../../../hooks/useAxios';
import {
  getExamCheck,
  getExamCheckReview,
  getExamCheckStart,
  postExamCheckReview,
} from '../../../api/testCenter.api';
import {
  IExamCheck,
  IExamCheckStart,
  IGetExamCheck,
  IGetExamCheckStart,
} from '../../../models/testCenter.model';
import _ from 'lodash';
import { Table, TableColumnsType } from 'antd';
import Button from '../../Button';
import Loader from '../../Loader';
import ErrorResponse from '../../ErrorResponse';
import InspectionModal from './modals/InspectionModal';
import useNotification from '../../../hooks/useNotification';
import useAuth from '../../../hooks/useAuth';
import moment from 'moment';
import AnswerModal from '../../modals/AnswerModal';

interface IProps {
  examCode?: number;
}

const TestInspection: FC<IProps> = ({ examCode }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetExamCheck>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetExamCheckStart>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetExamCheckStart>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [data, setData] = useState<IExamCheck[]>([]);
  const [visible, setVisible] = useState(false);
  const [answerVisible, setAnswerVisible] = useState(false);
  const [examTestCode, setExamTestCode] = useState<number | undefined>();
  const [checkType, setCheckType] = useState<string | undefined>();
  const [examCheckStart, setExamCheckStart] = useState<
    IExamCheckStart | undefined
  >();

  useEffect(() => {
    if (examCode) {
      execute(getExamCheck(examCode));
    }
  }, [examCode]);
  
  useEffect(() => {
    if (res && isLoading) {
      const data: any = _.cloneDeep(res.data);
      data.forEach((item: any) => {
        item.checkType = item.checkType === 'TE' ? t('cbt.test.4') : t('cbt.test.5');
      });
      setData(data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (res2) {
      setVisible(true);
      setExamCheckStart(res2.data);
      setExamTestCode(res2.data?.examTestCode);
      setCheckType(res2.data?.checkType);
    }
  }, [res2]);

  useEffect(() => {
    if (res3) {
      setVisible(true);
      setExamCheckStart(res3.data);
    }
  }, [res3]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      examCode && execute(getExamCheck(examCode));
    }
  }, [submitRes, submitIsLoading]);

  const onInspectionClick = () => {
    examCode && execute2(getExamCheckStart(examCode));
  }

  const seeAnswer = (examTestCode: number) => {
    setExamTestCode(examTestCode);
    setAnswerVisible(true);
  };

  const reExamination = (examTestCode: number, checkType: string) => {
    execute3(getExamCheckReview(examTestCode));
    setExamTestCode(examTestCode);
    checkType === t('cbt.test.5') ? setCheckType('FN') : setCheckType('TE');
  };

  const onOk = () => {
    if (examTestCode && checkType) {
      submitExecute(postExamCheckReview(examTestCode, checkType));
      closeModal();
    }
  };

  const columns: TableColumnsType<IExamCheck> = [
    {
      title: t('cbt.test.column23'),
      dataIndex: 'checkDate',
      align: 'center',
      render: value => moment(value).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: t('cbt.test.column24'),
      dataIndex: 'memKorName',
      align: 'center',
    },
    {
      title: t('cbt.test.6'),
      dataIndex: 'checkType',
      align: 'center',
    },
    {
      title: t('cbt.test.column29'),
      align: 'center',
      render: ({ examTestCode, checkType }: IExamCheck) =>
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic pink"
            text={t('cbt.test.column29')}
            onClick={() => reExamination(examTestCode, checkType)}
            writeAuthYn={writeAuthYn}
          />
        </div>
    },
    {
      title: t('cbt.test.column30'),
      align: 'center',
      render: ({ examTestCode }: IExamCheck) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic pink-line"
            text={t('cbt.test.button4')}
            onClick={() => seeAnswer(examTestCode)}
          />
        </div>
      ),
    },
  ];

  const closeModal = () => setVisible(false);

  const closeAnswerModal = () => setAnswerVisible(false);

  return (
    <>
      {isLoading || isLoading2 || isLoading3 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={submitError} />
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            text={t('cbt.test.button5')}
            onClick={onInspectionClick}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      <div className='comm-table__area'>
        <Table columns={columns} dataSource={data} />
      </div>
      {visible && examCheckStart && (
        <InspectionModal
          {...examCheckStart}
          visible={visible}
          onCancel={closeModal}
          onOk={onOk}
          checkType2={checkType}
          setCheckType={setCheckType}
        />
      )}
      {answerVisible && examCode && examTestCode && (
        <AnswerModal
          examCode={examCode}
          examTestCode={examTestCode}
          visible={answerVisible}
          onCancel={closeAnswerModal}
          isFullMode={false}
        />
      )}
    </>
  );
};

export default TestInspection;
